<template>
  <div :class="['support-inbox', { 'mobile-detail-open': mobileDetailOpen }]">
    <div class="inbox-head">
      <div class="inbox-title-wrap">
        <h1>Коммуникации</h1>
        <p>
          Единая рабочая очередь iWALY, WhatsApp, SMS, Push и внешних обращений.
        </p>
      </div>

      <div class="inbox-refresh">
        <span class="auto-refresh">
          {{ filter === 'archive'
            ? 'Архив · без автообновления'
            : (autoRefreshActive ? 'Авто · 15 сек' : 'Авто пауза') }}
        </span>
        <button
          class="btn btn-sm btn-outline-primary"
          :disabled="loading"
          @click="load(true)">
          Обновить
        </button>
      </div>
    </div>

    <div class="communications-workspace">
      <section class="queue-pane">
        <div class="queue-tools">
          <div class="filters">
            <button
              v-for="item in filters"
              :key="item.key"
              type="button"
              :class="['filter-btn', { active: filter === item.key }]"
              @click="setFilter(item.key)">
              {{ item.label }}
            </button>
          </div>

          <div v-if="filter !== 'archive'" class="role-filters">
            <button
              v-for="item in roleFilters"
              :key="item.key"
              type="button"
              :class="['role-filter', { active: roleFilter === item.key }]"
              @click="roleFilter = item.key">
              {{ item.label }}
            </button>
          </div>

          <div class="queue-search-row">
            <input
              v-model.trim="search"
              class="search"
              placeholder="Имя, телефон, e-mail или ID"
              @keyup.enter="load">
            <button class="btn btn-primary btn-sm" @click="load">
              Найти
            </button>
          </div>

          <div class="queue-summary">
            <span v-if="filter === 'archive'">
              <b>{{ visibleItems.length }}</b> из <b>{{ archiveTotal }}</b> диалогов
            </span>
            <span v-else><b>{{ visibleItems.length }}</b> диалогов</span>
            <span v-if="filter !== 'archive' && attentionCount">
              · <b>{{ attentionCount }}</b> требуют внимания
            </span>
          </div>
        </div>

        <div v-if="loading && items.length === 0" class="queue-empty">
          Загрузка…
        </div>

        <div v-else-if="visibleItems.length === 0" class="queue-empty">
          Подходящих обращений нет.
        </div>

        <div v-else class="inbox-list">
          <button
            v-for="item in visibleItems"
            :key="itemKey(item)"
            type="button"
            :class="[
              'conversation',
              `role-${item.role || 'external'}`,
              {
                attention: needsAttention(item),
                selected: selectedKey === itemKey(item),
              },
            ]"
            @click="selectConversation(item)">

            <span v-if="needsAttention(item)" class="attention-bar"></span>

            <div class="conversation-main">
              <div class="conversation-title">
                <span class="role-label">{{ item.roleLabel }}</span>
                <b class="conversation-name">{{ item.name }}</b>
              </div>

              <div class="status-row">
                <span v-if="item.historicalArchive" class="archive-badge">АРХИВ</span>

                <span
                  v-if="item.identityPresentation"
                  :class="['identity-badge', identityPresentationTone(item.identityPresentation)]">
                  {{ item.identityPresentation.label }}
                </span>

                <span
                  v-else-if="item.role === 'external'"
                  :class="['identity-badge', identityTone(item.identityStatus)]">
                  {{ identityLabel(item.identityStatus) }}
                </span>

                <span v-if="item.unreadCount" class="unread">
                  {{ item.unreadCount }} непрочит.
                </span>

                <span v-if="item.waitingForReply" class="waiting">
                  ждёт ответа
                </span>
              </div>

              <div class="snippet">{{ item.snippet || 'Вложение' }}</div>
              <div v-if="item.lastLoginAt && item.role !== 'external'" class="conversation-foot">
                <span>Вход: {{ formatCompactDate(item.lastLoginAt) }} · {{ item.lastLoginPlatform || item.platform || 'платформа неизвестна' }}</span>
              </div>

              <div class="conversation-foot">
                <span>{{ itemMeta(item) }}</span>
                <span>{{ formatCompactDate(item.lastMessageAt) }}</span>
              </div>
            </div>
          </button>
        </div>

        <div v-if="filter === 'archive' && archiveHasMore" class="archive-more">
          <button
            type="button"
            class="btn btn-sm btn-outline-primary"
            :disabled="archiveLoadingMore"
            @click="loadMoreArchive">
            {{ archiveLoadingMore ? 'Загрузка…' : 'Показать ещё' }}
          </button>
        </div>
      </section>

      <section class="detail-pane">
        <div v-if="selectedItem" class="detail-shell">
          <div class="detail-head">
            <button
              type="button"
              class="mobile-back"
              @click="mobileDetailOpen = false">
              ← Диалоги
            </button>

            <div class="detail-identity">
              <span :class="['detail-role', `role-${selectedItem.role || 'external'}`]">
                {{ selectedItem.roleLabel }}
              </span>
              <div class="detail-name-wrap">
                <b>{{ selectedItem.name }}</b>
                <span>{{ detailSubtitle(selectedItem) }}</span>
              </div>
            </div>

            <div class="detail-head-actions">
              <span v-if="selectedItem.unreadCount" class="unread">
                {{ selectedItem.unreadCount }} непрочит.
              </span>
              <span v-if="selectedItem.waitingForReply" class="waiting">
                ждёт ответа
              </span>

              <router-link
                :to="standaloneRoute(selectedItem)"
                class="btn btn-sm btn-outline-primary detail-open-link">
                <span class="detail-open-desktop">
                  {{ selectedItem.role === 'external' ? 'Открыть отдельно' : 'Карточка пользователя' }}
                </span>
                <span class="detail-open-mobile">
                  {{ selectedItem.role === 'external' ? 'Открыть' : 'Карточка' }}
                </span>
              </router-link>
            </div>
          </div>

          <div class="detail-body">
            <UserCommunications
              v-if="selectedItem.role !== 'external'"
              :key="`known-${itemKey(selectedItem)}`"
              :user-id="selectedItem.userId"
              :recipient-type="selectedItem.role"
              :recipient-label="selectedItem.role === 'contractor' ? 'исполнителю' : 'заказчику'"
              :embedded="true"
              @read="handleConversationRead" />

            <SupportExternalConversation
              v-else
              :key="`external-${selectedItem.externalContactId}`"
              :external-contact-id="selectedItem.externalContactId"
              :embedded="true"
              @read="handleConversationRead" />
          </div>
        </div>

        <div v-else class="detail-empty">
          <div class="detail-empty-icon">💬</div>
          <b>Выберите диалог</b>
          <span>История и поле ответа откроются здесь.</span>
        </div>
      </section>
    </div>
  </div>
</template>

<script>
import moment from 'moment';
import supportInbox from '@/api/support';
import UserCommunications from '@/components/User/UserCommunications.vue';
import SupportExternalConversation from '@/pages/SupportExternalConversation.vue';
import { iwalyNotify } from '@/notify';

export default {
  components: {
    UserCommunications,
    SupportExternalConversation,
  },

  data() {
    return {
      loading: false,
      filter: 'attention',
      roleFilter: 'all',
      search: '',
      items: [],
      selectedKey: null,
      selectedSnapshot: null,
      mobileDetailOpen: false,
      pollTimer: null,
      autoRefreshActive: true,
      archiveTotal: 0,
      archiveHasMore: false,
      archiveLoadingMore: false,

      filters: [
        { key: 'attention', label: 'Требуют внимания' },
        { key: 'unread', label: 'Непрочитанные' },
        { key: 'new', label: 'Ждут ответа' },
        { key: 'all', label: 'Все' },
        { key: 'archive', label: 'Архив Talk-Me' },
      ],

      roleFilters: [
        { key: 'all', label: 'Все роли' },
        { key: 'customer', label: 'Заказчики' },
        { key: 'contractor', label: 'Исполнители' },
        { key: 'external', label: 'Внешние' },
      ],
    };
  },

  computed: {
    visibleItems() {
      return this.items
        .filter((item) => this.roleFilter === 'all' || item.role === this.roleFilter)
        .slice()
        .sort((left, right) => {
          const leftPriority = this.needsAttention(left)
            || this.itemKey(left) === this.selectedKey;
          const rightPriority = this.needsAttention(right)
            || this.itemKey(right) === this.selectedKey;
          const priority = Number(rightPriority) - Number(leftPriority);

          if (priority !== 0) return priority;

          return moment(right.lastMessageAt).valueOf()
            - moment(left.lastMessageAt).valueOf();
        });
    },

    attentionCount() {
      return this.visibleItems.filter((item) => this.needsAttention(item)).length;
    },

    selectedItem() {
      if (!this.selectedKey) return null;

      const current = this.items.find(
        (item) => this.itemKey(item) === this.selectedKey,
      );

      if (current) return current;

      if (
        this.selectedSnapshot
        && this.itemKey(this.selectedSnapshot) === this.selectedKey
      ) {
        return this.selectedSnapshot;
      }

      return null;
    },
  },

  watch: {
    roleFilter() {
      this.$nextTick(this.ensureSelection);
    },
  },

  mounted() {
    this.load();

    document.addEventListener('visibilitychange', this.handleVisibilityChange);

    this.pollTimer = window.setInterval(() => {
      if (document.visibilityState === 'visible' && this.filter !== 'archive') {
        this.load(false);
      }
    }, 15000);
  },

  beforeDestroy() {
    document.removeEventListener('visibilitychange', this.handleVisibilityChange);

    if (this.pollTimer) {
      window.clearInterval(this.pollTimer);
    }
  },

  methods: {
    async inbox(filter, offset = 0) {
      const response = await supportInbox({
        filter,
        search: this.search || undefined,
        limit: 100,
        offset: filter === 'archive' ? offset : undefined,
      });

      return response.data || {};
    },

    mergeAttention(unread, waiting) {
      const byId = {};

      unread.concat(waiting).forEach((item) => {
        byId[this.itemKey(item)] = item;
      });

      return Object.keys(byId).map((key) => byId[key]);
    },

    async load(showSpinner = true, preserveSelection = true) {
      if (showSpinner) {
        this.loading = true;
      }

      const selectedBeforeReload = preserveSelection && this.selectedItem
        ? { ...this.selectedItem }
        : null;

      try {
        let nextItems = [];

        if (this.filter === 'attention') {
          const responses = await Promise.all([
            this.inbox('unread'),
            this.inbox('new'),
          ]);

          nextItems = this.mergeAttention(
            responses[0].items || [],
            responses[1].items || [],
          );
          this.archiveTotal = 0;
          this.archiveHasMore = false;
        } else if (this.filter === 'archive') {
          const archive = await this.inbox('archive', 0);
          nextItems = archive.items || [];
          this.archiveTotal = Number(archive.total || 0);
          this.archiveHasMore = !!archive.hasMore;
        } else {
          const response = await this.inbox(this.filter);
          nextItems = response.items || [];
          this.archiveTotal = 0;
          this.archiveHasMore = false;
        }

        if (
          selectedBeforeReload
          && this.selectedKey
          && !nextItems.some((item) => this.itemKey(item) === this.selectedKey)
        ) {
          nextItems.push(selectedBeforeReload);
        }

        this.items = nextItems;

        if (this.selectedKey) {
          const selectedAfterReload = this.items.find(
            (item) => this.itemKey(item) === this.selectedKey,
          );

          this.selectedSnapshot = selectedAfterReload
            ? { ...selectedAfterReload }
            : selectedBeforeReload;
        } else {
          this.selectedSnapshot = null;
        }

        this.$nextTick(this.ensureSelection);
      } catch (error) {
        iwalyNotify.error({
          text: 'Не удалось загрузить входящие обращения',
        });
      } finally {
        if (showSpinner) {
          this.loading = false;
        }
      }
    },

    async loadMoreArchive() {
      if (this.filter !== 'archive' || !this.archiveHasMore || this.archiveLoadingMore) return;

      this.archiveLoadingMore = true;

      try {
        const archive = await this.inbox('archive', this.items.length);
        const existing = new Set(this.items.map((item) => this.itemKey(item)));
        const additional = (archive.items || []).filter(
          (item) => !existing.has(this.itemKey(item)),
        );

        this.items = this.items.concat(additional);
        this.archiveTotal = Number(archive.total || this.archiveTotal);
        this.archiveHasMore = !!archive.hasMore;
      } catch (error) {
        iwalyNotify.error({ text: 'Не удалось загрузить архив Talk-Me' });
      } finally {
        this.archiveLoadingMore = false;
      }
    },

    ensureSelection() {
      if (this.visibleItems.length === 0) {
        this.selectedKey = null;
        this.selectedSnapshot = null;
        return;
      }

      const selectedStillVisible = this.visibleItems.some(
        (item) => this.itemKey(item) === this.selectedKey,
      );

      if (!selectedStillVisible) {
        const first = this.visibleItems[0];
        this.selectedKey = this.itemKey(first);
        this.selectedSnapshot = { ...first };
        return;
      }

      const selected = this.visibleItems.find(
        (item) => this.itemKey(item) === this.selectedKey,
      );

      if (selected) {
        this.selectedSnapshot = { ...selected };
      }
    },

    selectConversation(item) {
      const previousKey = this.selectedKey;
      const previousItem = this.selectedItem;

      this.selectedKey = this.itemKey(item);
      this.selectedSnapshot = { ...item };
      this.mobileDetailOpen = true;

      if (
        previousKey
        && previousKey !== this.selectedKey
        && previousItem
        && !this.matchesActiveFilter(previousItem)
      ) {
        this.items = this.items.filter(
          (candidate) => this.itemKey(candidate) !== previousKey,
        );
      }
    },

    async handleConversationRead() {
      const selected = this.items.find(
        (item) => this.itemKey(item) === this.selectedKey,
      );

      if (selected) {
        selected.unreadCount = 0;
        this.selectedSnapshot = { ...selected };
      } else if (this.selectedSnapshot) {
        this.selectedSnapshot = {
          ...this.selectedSnapshot,
          unreadCount: 0,
        };
      }

      await this.load(false, true);
    },

    itemKey(item) {
      if (item && item.historicalArchive && item.externalContactId) {
        return `archive-external-${item.externalContactId}`;
      }
      if (item && item.userId && ['customer', 'contractor'].includes(item.role)) {
        return `user-${item.role}-${item.userId}`;
      }
      return item && item.externalContactId
        ? `external-${item.externalContactId}`
        : `conversation-${item.conversationId}`;
    },

    needsAttention(item) {
      return !!(item && (item.unreadCount || item.waitingForReply));
    },

    matchesActiveFilter(item) {
      if (!item) return false;
      if (this.filter === 'all') return true;
      if (this.filter === 'unread') return Number(item.unreadCount || 0) > 0;
      if (this.filter === 'new') return !!item.waitingForReply;

      return this.needsAttention(item);
    },

    handleVisibilityChange() {
      this.autoRefreshActive = document.visibilityState === 'visible';

      if (this.autoRefreshActive && this.filter !== 'archive') {
        this.load(false);
      }
    },

    setFilter(value) {
      this.filter = value;
      if (value === 'archive') this.roleFilter = 'all';
      this.archiveTotal = 0;
      this.archiveHasMore = false;
      this.selectedKey = null;
      this.selectedSnapshot = null;
      this.mobileDetailOpen = false;
      this.load(true, false);
    },

    standaloneRoute(item) {
      if (item.role === 'external') {
        return {
          name: 'communications.external',
          params: { id: item.externalContactId },
        };
      }

      return {
        name: item.role === 'contractor'
          ? 'contractors.details.communications'
          : 'customers.details.communications',
        params: { id: item.userId },
      };
    },

    itemMeta(item) {
      if (item.historicalArchive) {
        return item.userId && item.identityPresentation
          ? `User #${item.userId} · ${item.identityPresentation.label} · архив Talk-Me`
          : `Talk-Me · архив · #${item.externalContactId}`;
      }

      if (item.role === 'external') {
        const channel = item.identityPresentation && item.identityPresentation.channel;
        return `${this.externalChannelLabel(channel)} · #${item.externalContactId}`;
      }

      if (item.identityPresentation && item.identityPresentation.level !== 'confirmed') {
        return `User #${item.userId} · ${item.identityPresentation.label} · ${this.externalChannelLabel(item.identityPresentation.channel)}`;
      }

      const device = this.deviceLabel(item);
      return device === 'версия приложения неизвестна'
        ? `User #${item.userId}`
        : `User #${item.userId} · ${device}`;
    },

    detailSubtitle(item) {
      if (item.historicalArchive) {
        return item.userId && item.identityPresentation
          ? `User #${item.userId} · ${item.identityPresentation.label} · исторический архив`
          : 'Talk-Me · исторический архив';
      }

      if (item.role === 'external') {
        const channel = item.identityPresentation && item.identityPresentation.channel;
        return `${this.externalChannelLabel(channel)} · внешний контакт`;
      }

      if (item.identityPresentation && item.identityPresentation.level !== 'confirmed') {
        return `User #${item.userId} · ${item.identityPresentation.label} · ${this.externalChannelLabel(item.identityPresentation.channel)}`;
      }

      return `User #${item.userId} · ${this.deviceLabel(item)}`;
    },

    formatCompactDate(value) {
      const date = moment(value);
      if (!date.isValid()) return '—';

      if (date.isSame(moment(), 'day')) {
        return date.format('HH:mm');
      }

      if (date.isSame(moment().subtract(1, 'day'), 'day')) {
        return `вчера ${date.format('HH:mm')}`;
      }

      return date.format('DD.MM HH:mm');
    },

    deviceLabel(item) {
      if (item.role === 'external') {
        return 'Talk-Me';
      }

      const parts = [
        item.platform,
        item.appVersion,
        item.appBuild ? `(${item.appBuild})` : null,
      ].filter(Boolean);

      return parts.length
        ? parts.join(' ')
        : 'версия приложения неизвестна';
    },

    identityLabel(status) {
      return {
        unverified_match: 'личность не подтверждена',
        conflict: 'конфликт данных',
        ambiguous: 'неоднозначно',
        guest: 'внешний источник',
        verified: 'подтверждено',
      }[status] || 'внешний источник';
    },

    identityPresentationTone(presentation) {
      const level = presentation && presentation.level;
      if (level === 'conflict') return 'danger';
      if (level === 'confirmed' || level === 'channel_confirmed') return 'success';
      return 'warning';
    },

    externalChannelLabel(channel) {
      return {
        web: 'Web · Talk-Me',
        max: 'MAX · Talk-Me',
        whatsapp: 'WhatsApp',
        email: 'Email · Talk-Me',
        vk: 'VK · Talk-Me',
        meta: 'Facebook + Instagram · Talk-Me',
        telegram: 'Telegram · Talk-Me',
        viber: 'Viber · Talk-Me',
      }[channel] || 'Talk-Me';
    },

    identityTone(status) {
      if (status === 'conflict' || status === 'ambiguous') {
        return 'danger';
      }

      if (status === 'verified') {
        return 'success';
      }

      return 'warning';
    },
  },
};
</script>

<style scoped>
.support-inbox {
  width: 100%;
  max-width: none;
  min-width: 0;
  min-height: 0;
  height: 100%;
  flex: 1 1 auto;
  display: flex;
  flex-direction: column;
  box-sizing: border-box;
  padding: 18px 18px 18px 20px;
  overflow: hidden;
}

.inbox-head {
  flex: 0 0 auto;
  display: flex;
  justify-content: space-between;
  gap: 20px;
  align-items: flex-start;
  margin-bottom: 14px;
}

.inbox-title-wrap {
  min-width: 0;
  flex: 1;
}

.inbox-head h1 {
  margin: 0 0 3px;
  font-size: 24px;
}

.inbox-head p {
  margin: 0;
  color: #7b8493;
  font-size: 12px;
}

.inbox-refresh {
  display: flex;
  gap: 8px;
  align-items: center;
}

.auto-refresh {
  color: #8a929e;
  font-size: 10px;
  white-space: nowrap;
}

.communications-workspace {
  flex: 1 1 auto;
  display: grid;
  grid-template-columns: minmax(320px, 31%) minmax(0, 69%);
  gap: 12px;
  height: auto;
  min-height: 0;
  max-height: 100%;
}

.queue-pane,
.detail-pane {
  min-width: 0;
  min-height: 0;
  border: 1px solid #e0e4eb;
  border-radius: 12px;
  background: #fff;
  overflow: hidden;
}

.queue-pane {
  display: flex;
  flex-direction: column;
}

.queue-tools {
  padding: 11px 11px 9px;
  border-bottom: 1px solid #edf0f4;
  background: #fff;
}

.filters,
.role-filters {
  display: flex;
  gap: 6px;
  overflow-x: auto;
  padding-bottom: 1px;
  scrollbar-width: thin;
}

.role-filters {
  margin-top: 7px;
}

.filter-btn,
.role-filter {
  flex: 0 0 auto;
  border: 1px solid #d8dde6;
  background: #fff;
  border-radius: 999px;
  white-space: nowrap;
  cursor: pointer;
}

.filter-btn {
  padding: 6px 10px;
  font-size: 11px;
}

.role-filter {
  padding: 4px 8px;
  color: #677386;
  font-size: 9px;
}

.filter-btn.active {
  border-color: #3d9b6d;
  color: #25704d;
  background: #edf8f2;
}

.role-filter.active {
  border-color: #8ea9bd;
  color: #365b75;
  background: #f1f7fb;
}

.queue-search-row {
  display: flex;
  gap: 6px;
  margin-top: 8px;
}

.search {
  min-width: 0;
  width: 100%;
  flex: 1;
  border: 1px solid #d8dde6;
  border-radius: 8px;
  padding: 7px 9px;
  font-size: 11px;
}

.queue-summary {
  margin-top: 7px;
  color: #7a8492;
  font-size: 10px;
}

.archive-more {
  flex: 0 0 auto;
  padding: 8px 10px;
  border-top: 1px solid #edf0f4;
  background: #fff;
  text-align: center;
}

.archive-badge {
  border-radius: 999px;
  padding: 3px 7px;
  background: #eef1f5;
  color: #596577;
  font-size: 9px;
  font-weight: 800;
  letter-spacing: .04em;
}

.inbox-list {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  overscroll-behavior: contain;
  background: #f8fafb;
}

.queue-empty {
  padding: 32px 16px;
  color: #7b8493;
  font-size: 12px;
  text-align: center;
}

.conversation {
  position: relative;
  width: 100%;
  border: 0;
  border-bottom: 1px solid rgba(219, 224, 231, .78);
  text-align: left;
  padding: 12px 12px 11px 15px;
  cursor: pointer;
  transition:
    background-color .15s ease,
    box-shadow .15s ease,
    transform .15s ease;
}

.conversation:last-child {
  border-bottom: 0;
}

/*
 * ROLE = translucent whole-row color.
 * ATTENTION = orange vertical line only.
 * They are intentionally separate visual dimensions.
 */
.conversation.role-customer {
  background: #e4f3ea;
}

.conversation.role-contractor {
  background: #e5eefb;
}

.conversation.role-external {
  background: #edf0f3;
}

.conversation.role-customer:hover {
  background: #d8ede1;
}

.conversation.role-contractor:hover {
  background: #d9e7f8;
}

.conversation.role-external:hover {
  background: #e2e6ea;
}

.conversation.selected {
  box-shadow:
    inset 0 0 0 2px rgba(38, 54, 72, .28),
    0 2px 5px rgba(28, 42, 58, .08);
  z-index: 1;
}

.attention-bar {
  position: absolute;
  left: 0;
  top: 0;
  bottom: 0;
  width: 4px;
  background: #e4a03b;
}

.conversation-main {
  min-width: 0;
}

.conversation-title {
  display: flex;
  align-items: baseline;
  gap: 7px;
  min-width: 0;
}

.role-label {
  flex: 0 0 auto;
  border-radius: 5px;
  padding: 3px 6px;
  color: #fff;
  font-size: 8px;
  font-weight: 900;
  line-height: 12px;
  letter-spacing: .055em;
  text-transform: uppercase;
  box-shadow: 0 1px 1px rgba(23, 34, 47, .08);
}

.role-customer .role-label {
  background: #2c8c58;
}

.role-contractor .role-label {
  background: #356fb8;
}

.role-external .role-label {
  background: #687482;
}

.conversation-name {
  min-width: 0;
  overflow: hidden;
  color: #273445;
  font-size: 13px;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.status-row {
  display: flex;
  flex-wrap: wrap;
  gap: 5px;
  min-height: 3px;
  margin-top: 5px;
}

.waiting,
.unread,
.identity-badge {
  border-radius: 999px;
  padding: 2px 6px;
  font-size: 9px;
  line-height: 14px;
  font-weight: 700;
}

.waiting {
  background: #fff0d5;
  color: #865d1f;
}

.unread {
  background: #e9f7ef;
  color: #23814b;
}

.identity-badge.warning {
  background: #fff3dc;
  color: #7b5b24;
}

.identity-badge.danger {
  background: #fdebea;
  color: #8b403b;
}

.identity-badge.success {
  background: #eaf7ef;
  color: #2f7650;
}

.snippet {
  margin-top: 6px;
  overflow: hidden;
  color: #394556;
  font-size: 11px;
  line-height: 15px;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
}

.conversation-foot {
  display: flex;
  justify-content: space-between;
  gap: 8px;
  min-width: 0;
  margin-top: 7px;
  color: #8a929e;
  font-size: 9px;
}

.conversation-foot span:first-child {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.conversation-foot span:last-child {
  flex: 0 0 auto;
  white-space: nowrap;
}

.detail-pane {
  display: flex;
  flex-direction: column;
  background: #f8fafb;
}

.detail-shell {
  display: flex;
  flex: 1;
  min-height: 0;
  flex-direction: column;
}

.detail-head {
  min-height: 58px;
  padding: 9px 12px;
  border-bottom: 1px solid #e5e9ef;
  background: #fff;
  display: flex;
  align-items: center;
  gap: 10px;
}

.mobile-back {
  display: none;
  border: 0;
  background: transparent;
  color: #4d6f88;
  padding: 4px 3px;
  font-size: 11px;
  cursor: pointer;
}

.detail-identity {
  display: flex;
  align-items: center;
  gap: 9px;
  min-width: 0;
  flex: 1;
}

.detail-role {
  flex: 0 0 auto;
  border-radius: 7px;
  padding: 4px 7px;
  font-size: 9px;
  font-weight: 800;
}

.detail-role.role-customer {
  background: #2c8c58;
  color: #fff;
}

.detail-role.role-contractor {
  background: #356fb8;
  color: #fff;
}

.detail-role.role-external {
  background: #687482;
  color: #fff;
}

.detail-name-wrap {
  min-width: 0;
  display: flex;
  flex-direction: column;
}

.detail-name-wrap b {
  overflow: hidden;
  color: #263447;
  font-size: 14px;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.detail-name-wrap span {
  overflow: hidden;
  color: #8a929e;
  font-size: 9px;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.detail-head-actions {
  display: flex;
  align-items: center;
  gap: 6px;
  flex: 0 0 auto;
}

.detail-open-mobile {
  display: none;
}

.detail-body {
  flex: 1 1 auto;
  min-height: 0;
  min-width: 0;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

.detail-body > * {
  flex: 1 1 auto;
  min-height: 0;
  min-width: 0;
  width: 100%;
}

.detail-empty {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  gap: 5px;
  color: #7f8997;
  text-align: center;
}

.detail-empty-icon {
  font-size: 28px;
  opacity: .5;
}

@media (max-width: 1180px) {
  .communications-workspace {
    grid-template-columns: minmax(300px, 36%) minmax(0, 64%);
  }

  .detail-head-actions .waiting,
  .detail-head-actions .unread {
    display: none;
  }
}

@media (max-width: 900px) {
  .support-inbox {
    height: auto;
    min-height: 0;
    flex: none;
    padding: 10px;
    overflow: visible;
  }

  .inbox-head {
    margin-bottom: 9px;
  }

  .inbox-head h1 {
    font-size: 19px;
  }

  .inbox-head p,
  .auto-refresh {
    display: none;
  }

  /* In detail mode the global Admin header already says "Коммуникации".
     Hide the duplicate page title/refresh row and give the chat the space. */
  .support-inbox.mobile-detail-open {
    padding-top: 4px;
  }

  .support-inbox.mobile-detail-open .inbox-head {
    display: none;
  }

  .communications-workspace {
    display: block;
    height: auto;
    min-height: 0;
  }

  .queue-pane,
  .detail-pane {
    min-height: calc(100dvh - 90px);
    border-radius: 10px;
  }

  .support-inbox.mobile-detail-open .detail-pane {
    min-height: calc(100dvh - 58px);
  }

  .detail-pane {
    display: none;
  }

  .support-inbox.mobile-detail-open .queue-pane {
    display: none;
  }

  .support-inbox.mobile-detail-open .detail-pane {
    display: flex;
  }

  .mobile-back {
    display: inline-flex;
  }

  .detail-head {
    min-height: 50px;
    padding: 6px 8px;
    gap: 7px;
  }

  .detail-head-actions .btn {
    padding: 6px 8px;
    font-size: 9px;
  }

  .detail-open-desktop {
    display: none;
  }

  .detail-open-mobile {
    display: inline;
  }

  .inbox-list {
    max-height: calc(100dvh - 245px);
  }
}

@media (max-width: 560px) {
  .detail-role {
    display: none;
  }

  .detail-head {
    padding: 5px 7px;
  }

  .detail-name-wrap span {
    display: none;
  }

  .detail-name-wrap b {
    font-size: 13px;
  }

  .mobile-back {
    font-size: 10px;
    white-space: nowrap;
  }

  .detail-head-actions .btn {
    max-width: 92px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
}
</style>
